import type { FieldDefinition } from '@/types'

// ── Use/Type Subtype fields ────────────────────────────────────
// Not part of TRUCK_STANDARD_FIELDS/TRAILER_STANDARD_FIELDS — useConditionalFields.ts
// injects these as the first field in 'body-information' only when the selected Use/Type
// actually has Subtypes, with `refCategory` filled in dynamically per the current Use/Type.
// `formSection: 'standard-equipment'` means the value is stored alongside Use/Type, so a
// Use/Type change resets it the same way and claimPayloadMapper.ts needs no special-casing.
export const TRUCK_USE_SUBTYPE_FIELD: FieldDefinition = {
    id: 'useSubtype', label: 'Sub Type', type: 'select', required: true,
    options: ['Select'], formSection: 'standard-equipment',
}

export const TRAILER_TYPE_SUBTYPE_FIELD: FieldDefinition = {
    id: 'typeSubtype', label: 'Sub Type', type: 'select', required: true,
    options: ['Select'], formSection: 'standard-equipment',
}

// ── Truck/Tractor Standard Equipment fields ───────────────────
export const TRUCK_STANDARD_FIELDS: FieldDefinition[] = [

    { id: 'vin', label: 'VIN#', type: 'text', required: true, maxLength: 17},

    { 
        id: 'year', label: 'Year', type: 'select', required: true,
        options: ['Select Year', ...Array.from({ length: 97 }, (_, i) => String(new Date().getFullYear() - i))]
    },

    {
        id: 'make', label: 'Make', type: 'select', required: true,
        options: ['Select']
    },

    {
        id: 'model', label: 'Model', type: 'select', required: true,
        options: ['Select']
    },

    {
        id: 'style', label: 'Style', type: 'select',
        options: ['Select']
    },

    {
        id: 'truck_type', label: 'Type', type: 'select',
        options: ['Select']
    },

    {
        id: 'use', label: 'Use', type: 'select', required: true,
        options: ['Select']
    },

    {
        id: 'engineMfg', label: 'Engine Mfg', type: 'select',
        options: ['Select']
    },

    {
        id: 'engineModel', label: 'Engine Model', type: 'select',
        options: ['Select']
    },

    {
        id: 'engineHP', label: 'Engine HP', type: 'select',
        options: ['Select']
    },

    {
        id: 'transMfg', label: 'Trans Mfg', type: 'select',
        options: ['Select']
    },

    {
        id: 'noOfSpeeds', label: '# of Speeds', type: 'select',
        options: ['Select']
    },

    { id: 'transModel', label: 'Trans Model', type: 'text' },

    { id: 'noOfAxles', label: '# of Axles', type: 'text' },

    { id: 'noOfDriveAxles', label: '# of Drive Axles', type: 'text' },

    { id: 'noOfTagAxles', label: '# of Tag Axles', type: 'text' },

    {
        id: 'frontAxleRating', label: 'Front Axle Rating', type: 'select',
        options: ['Select']
    },

    {
        id: 'rearAxleRating', label: 'Rear Axle Rating', type: 'select',
        options: ['Select']
    },

    {
        id: 'gvw', label: 'GVW/GVWR', type: 'select', required: true,
        options: ['Select']
    },

    {
        id: 'branded', label: 'Branded/Previous Salvage Title', type: 'toggle'
    },

    { id: 'odometer', label: 'Odometer', type: 'number', placeholder: 'Leave blank if unknown' }
]

// ── Truck/Tractor Additional Options fields ────────────────────────
export const TRUCK_ADDITIONAL_FIELDS: FieldDefinition[] = [

    // Interior
    {
        id: 'convSleeperSize',
        label: 'Conv Sleeper Size',
        type: 'select',
        options: ['Select'],
        subHeader: 'Interior',
        required: true
    },

    {
        id: 'coeSize',
        label: 'COE Size',
        type: 'select',
        options: ['Select']
    },

    {
        id: 'apu',
        label: 'APU',
        type: 'select',
        options: ['Select']
    },

    {
        id: 'trimLevel',
        label: 'Trim Level',
        type: 'select',
        options: ['Select']
    },

    {
        id: 'gps',
        label: 'GPS',
        type: 'toggle',
    },

    {
        id: 'airRideCab',
        label: 'Air Ride Cab',
        type: 'toggle',
    },

    {
        id: 'airConditioning',
        label: 'Air Conditioning',
        type: 'toggle',
    },

    // Exterior
    {
        id: 'frontWheelType',
        label: 'Front Wheel Type',
        type: 'select',
        options: ['Select'],
        subHeader: 'Exterior',
    },

    {
        id: 'osRearWheelType',
        label: 'O/S Rear Wheel Type',
        type: 'select',
        options: ['Select'],
    },

    {
        id: 'isRearWheelType',
        label: 'I/S Rear Wheel Type',
        type: 'select',
        options: ['Select'],
    },

    {
        id: 'frontTireSize',
        label: 'Front Tire Size',
        type: 'select',
        options: ['Select']
    },

    {
        id: 'rearTireSize',
        label: 'Rear Tire Size',
        type: 'select',
        options: ['Select']
    },

    {
        id: 'suspension',
        label: 'Suspension',
        type: 'select',
        options: ['Select']
    },

    {
        id: 'liftGateCapacity',
        label: 'Lift Gate Capacity',
        type: 'select',
        options: ['Select']
    },

    {
        id: 'fifthWheelType',
        label: '5th Wheel Type',
        type: 'select',
        options: ['Select']
    },

    {
        id: 'hitchType',
        label: 'Hitch Type',
        type: 'select',
        options: ['Select']
    },

    {
        id: 'pto',
        label: 'PTO',
        type: 'toggle'
    },

    {
        id: 'wetLineKit',
        label: 'Wet Line Kit (Tractor Only)',
        type: 'toggle'
    },

    {
        id: 'airBrakes',
        label: 'Air Brakes',
        type: 'toggle'
    },

    {
        id: 'engineBrake',
        label: 'Engine Brake',
        type: 'toggle'
    },

    {
        id: 'sideFairing',
        label: 'Side Fairing',
        type: 'toggle',
        subHeader: "Aerodynamics"
    },

    {
        id: 'windDeflector',
        label: 'Wind Deflector',
        type: 'toggle',
    },

    {
        id: 'tankFairings',
        label: 'Tank Fairings',
        type: 'toggle'
    },

    {
        id: 'fullAerodynamics',
        label: 'Full Aerodynamics',
        type: 'toggle',
    }

]

// ── Truck/Tractor vehicle Condition fields ────────────────────────
export const TRUCK_CONDITIONAL_FIELDS: FieldDefinition[] = [
    // Interior Section
    {
        id: 'interior', label: 'Interior', type: 'select', required: false, subHeader: 'Interior',
        options: ['Select']
    },

    // Exterior Section
    {
        id: 'body', label: 'Body', type: 'select', placeholder: 'Enter Body Condition', required: false, subHeader: 'Exterior',
        options: ['Select']
    },
    {
        id: 'interiorGlass', label: 'Glass', type: 'select', placeholder: 'Enter Glass Condition', required: false,
        options: ['Select']
    },
    {
        id: 'interiorPaint', label: 'Paint', type: 'select', placeholder: 'Enter Paint Condition', required: false,
        options: ['Select']
    },

    // Exterior / Drivetrain Section
    {
        id: 'drivetrainEngine', label: 'Engine', type: 'select', placeholder: 'Enter Engine Details', required: false,
        options: ['Select']
    },
    {
        id: 'drivetrainTransmission', label: 'Transmission', type: 'select', placeholder: 'Enter Transmission Details', required: false,
        options: ['Select']
    },
    // {
    //     id: 'drivetrainOverhaulType',
    //     label: 'Overhaul Type',
    //     type: 'select',
    //     placeholder: 'Select Overhaul Type',
    //     required: false,
    //     options: ['Select']
    // },
    // { id: 'drivetrainDateOfOverhaul', label: 'Date of Overhaul', type: 'date', placeholder: 'Select Date of Overhaul', required: false },
    // { id: 'drivetrainMileageSinceOverhaul', label: 'Mileage Since Overhaul', type: 'text', placeholder: 'Enter Mileage Since Overhaul', required: false },

    // Tires Section
    { id: 'tiresFWearRmg', label: 'F Wear Rmg %', type: 'text', placeholder: 'Enter Front Wear Remaining %', required: false, subHeader: 'Tires' },
    { id: 'tiresRWearRmg', label: 'R Wear Rmg %', type: 'text', placeholder: 'Enter Rear Wear Remaining %', required: false },
    { id: 'conditional_notes', label: 'Additional Notes', type: 'textarea', required: false }
]

// ── Trailer Standard Equipment fields ────────────────────────
export const TRAILER_STANDARD_FIELDS: FieldDefinition[] = [
    { id: 'vin', label: 'VIN#', type: 'text', placeholder: 'Enter VIN', required: true },
    { id: 'year', label: 'Year', type: 'select', required: true, options: ['Select Year', ...Array.from({ length: 30 }, (_, i) => String(2026 - i))] },
    {
        id: 'make', label: 'Make', type: 'select', required: true,
        options: ['Select']
    },

    {
        id: 'model', label: 'Model', type: 'select', required: true,
        options: ['Select']
    },
    {
        id: 'type',
        label: 'Type',
        type: 'select',
        placeholder: 'Select Type',
        required: true,
        options: ['Select']
    },
    {
        id: 'exterior', label: 'Exterior', type: 'select', required: false,
        options: ['Select']
    },
    { id: 'noOfAxles', label: '# of Axles', type: 'text', placeholder: 'Enter Number of Axles', required: true },
    {
        id: 'length', label: 'Length', type: 'select', required: false,
        options: ['Select']
    },
    { id: 'branded', label: 'Branded/Previous Salvage Title', type: 'toggle' },
]

// ── Trailer Additional Options fields ────────────────────────
export const TRAILER_ADDITIONAL_FIELDS: FieldDefinition[] = [
    {
        id: 'height', label: 'Height', type: 'select', placeholder: 'Enter Height', required: false, subHeader: 'Trailer Components',
        options: ['Select']
    },
    {
        id: 'width', label: 'Width', type: 'select', placeholder: 'Enter Width', required: false,
        options: ['Select']
    },
    {
        id: 'suspension',
        label: 'Suspension',
        type: 'select',
        placeholder: 'Select Suspension Type',
        required: false,
        options: ['Select']
    },
    {
        id: 'gawrGvv', label: 'GAWR/GVV', type: 'select', placeholder: 'Enter GAWR/GVV Rating', required: false,
        options: ['Select']
    },
    { id: 'totalAxleCapacity', label: 'Total Axle Capacity', type: 'text', placeholder: 'Enter Total Axle Capacity', required: false },
    {
        id: 'abs',
        label: 'ABS',
        type: 'toggle'
    },
    {
        id: 'interiorLining',
        label: 'Interior Lining',
        type: 'select',
        placeholder: 'Select Interior Lining',
        required: false,
        options: ['Select']
    },
    {
        id: 'rearDoorType',
        label: 'Rear Door Type',
        type: 'select',
        placeholder: 'Select Rear Door Type',
        required: false,
        options: ['Select']
    },
    {
        id: 'osWheelType',
        label: 'O/S Wheel Type',
        type: 'select',
        placeholder: 'Select O/S Wheel Type',
        required: false,
        options: ['Select']
    },
    {
        id: 'isWheelType',
        label: 'I/S Wheel Type',
        type: 'select',
        placeholder: 'Select I/S Wheel Type',
        required: false,
        options: ['Select']
    },
    {
        id: 'tireSize',
        label: 'Tire Size',
        type: 'select',
        placeholder: 'Select Tire Size',
        required: false,
        options: ['Select']
    }

]

// ── Trailer Vehicle Conditions fields ────────────────────────
export const TRAILER_CONDITIONAL_FIELDS: FieldDefinition[] = [
    // Interior Section
    {
        id: 'interiorLining',
        label: 'Lining',
        type: 'select',
        placeholder: 'Select Lining Condition',
        required: false,
        subHeader: 'Interior',
        options: ['Select']
    },

    // Exterior Section
    {
        id: 'exteriorBody',
        label: 'Body',
        type: 'select',
        placeholder: 'Select Body Condition',
        required: false,
        subHeader: 'Exterior',
        options: ['Select']
    },
    {
        id: 'exteriorPaint',
        label: 'Paint',
        type: 'select',
        placeholder: 'Select Paint Condition',
        required: false,
        options: ['Select']
    },

    // Mechanical Section
    {
        id: 'mechanicalHydraulics',
        label: 'Hydraulics',
        type: 'select',
        placeholder: 'Select Hydraulics Condition',
        required: false,
        subHeader: 'Mechanical',
        options: ['Select']
    },
    {
        id: 'mechanicalCoolingUnit',
        label: 'Cooling Unit',
        type: 'select',
        placeholder: 'Select Cooling Unit Condition',
        required: false,
        options: ['Select']
    },

    // Tires Section
    {
        id: 'tiresFWearRmg',
        label: 'F Wear Rmg %',
        type: 'text',
        placeholder: 'Enter Front Wear Remaining %',
        required: false,
        subHeader: 'Tires'
    },
    {
        id: 'tiresRWearRmg',
        label: 'R Wear Rmg %',
        type: 'text',
        placeholder: 'Enter Rear Wear Remaining %',
        required: false
    },
    { id: 'conditional_notes', label: 'Additional Notes', type: 'textarea', required: false }

]

// ── Body Information fields (Truck/Tractor) ───────────────────────────────────
// Truck body fields are now fully driven by the attribute_master API.
// See src/services/attributeService.ts and src/utils/attributeMapper.ts.
// The BODY_FIELDS_BY_USE record has been removed.

